package com.oasisfeng.invaluable.data

import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.data.Stream.Companion.EMPTY
import com.oasisfeng.invaluable.data.Stream.OctetStream.encode
import com.oasisfeng.invaluable.invoke
import com.oasisfeng.invaluable.isIn
import com.oasisfeng.invaluable.targetAsInt
import com.oasisfeng.invaluable.targetAsQuantity
import com.oasisfeng.invaluable.then
import kotlinx.io.bytestring.ByteString
import kotlin.jvm.JvmInline
import kotlin.math.pow

/**
 * Stream topology verified under a concrete digit alphabet.
 *
 * The underlying Link topology is the digit chain rooted at [EMPTY]. A [Stream] is the validated view identity over
 * that topology, so [radix] intentionally participates in equality: the same Link may be valid under multiple digit
 * alphabets, and those views have different downstream decoding guarantees.
 *
 * @property link Complete digit stream topology.
 * @property radix Verified digit alphabet size.
 * @property length Number of digits in [link].
 */
class Stream private constructor(override val link: NestedLink, val radix: Radix, val length: Int) : NestedLinkable {

    /** Require that [link] is a stream under [radix]. */
    constructor(link: NestedLink, radix: Radix) : this(link, radix, streamLengthOrNull(link, radix)
        ?: throw IllegalArgumentException("$link is not a digit stream in radix ${radix.value}."))

    /** Verified digit alphabet size for a [Stream] view. */
    @JvmInline value class Radix(val value: Int) { init { require(value >= 2) { "Radix must be at least 2." }}}
    /** Number of stream digits consumed by one fixed-width frame. */
    @JvmInline value class GroupWidth(val value: Int) { init { require(value >= 1) { "Grouping width must be positive." }}}

    /**
     * Fixed-width frame lens over a stream view.
     *
     * A framing describes how receivers should group a [Stream]'s verified digits into frames. It is not additional
     * stream topology by itself; [FixedWidthGrouping] is the Link-backed declaration of the same lens.
     */
    data class Framing(val radix: Radix, val width: GroupWidth) {
        /** Number of distinct frame values, or null when the product overflows [Int]. */
        val cardinality: Int? = (radix.value.toDouble().pow(width.value)).takeIf { it <= Int.MAX_VALUE }?.toInt()
        /** Whether this framing has exactly [cardinality] distinct frame values. */
        fun exactly(cardinality: Int): Boolean = this.cardinality == cardinality
    }

    companion object {
        /** Empty stream under given [radix]. */
        fun empty(radix: Radix): Stream = Stream(EMPTY, radix, 0)
        /** Empty stream topology. It is valid under every radix, but does not carry one by itself. */
        internal val EMPTY: NestedLink = Dimensions.DATA()

        fun NestedLink.asStream(radix: Radix): Stream? = Stream(this, radix, streamLengthOrNull(this, radix) ?: return null)

        private tailrec fun streamLengthOrNull(current: NestedLink, radix: Radix, length: Int = 0): Int? {
            if (current == EMPTY) return length
            val digit = current.targetAsInt() ?: return null
            if (digit !in 0 until radix.value) return null
            val parent = current.parent().nestedOrNull() ?: return null
            return streamLengthOrNull(parent, radix, length + 1)
        }
    }

    internal fun append(digit: Int): Stream {
        require(digit in 0 until radix.value) { "Digit $digit is outside radix ${radix.value}." }
        return Stream(link.then(Quantity(digit).link), radix, length + 1)
    }

    override fun equals(other: Any?): Boolean = other is Stream && radix == other.radix && link == other.link
    override fun hashCode(): Int = 31 * link.hashCode() + radix.hashCode()
    override fun toString(): String = link.toString()

    /**
     * Fixed-width grouping lens over a verified stream.
     *
     * Topology:
     *
     * ```text
     * stream -> DATA(radix / width)
     * ```
     *
     * The grouping marker is a derived decoder view, not a new stream identity. [stream] remains the durable digit
     * topology; [width] tells a renderer how many digits to read per frame.
     */
    class FixedWidthGrouping private constructor(override val link: NestedLink, val stream: Stream, val width: GroupWidth) : NestedLinkable {
        /** Build a fixed-width grouping lens over [stream]. */
        constructor(stream: Stream, width: GroupWidth)
            : this(stream.link.then(stream.appendLens(width)), stream, width)

        val framing: Framing = Framing(stream.radix, width)

        companion object {

            /** Validate and wrap [link] as a fixed-width grouping lens. */
            fun render(link: NestedLink): FixedWidthGrouping? {
                val target = link.nestedTargetOrNull() ?: return null
                if (!target.isIn(Dimensions.DATA)) return null
                val rational = target.targetAsQuantity() ?: return null
                val width = rational.denominator
                if (width < 1) return null
                val radix = rational.numerator
                if (radix < 2) return null
                return link.parentOrNull().nestedOrNull()?.asStream(Radix(radix))?.let { FixedWidthGrouping(it, GroupWidth(width)) }
            }

            private fun Stream.appendLens(width: GroupWidth): NestedLink
                = Dimensions.DATA(Quantity(radix.value).link.then(Quantity(width.value).link))

        }

        override fun equals(other: Any?): Boolean = other is FixedWidthGrouping && link == other.link
        override fun hashCode(): Int = link.hashCode()
        override fun toString(): String = link.toString()
    }

    /**
     * Binary stream codec.
     *
     * Topology:
     *
     * ```text
     * Stream.EMPTY -> 0 -> 1 -> ...
     * ```
     *
     * This deliberately makes "bit" a grammar position in a radix-2 stream instead of a standalone symbol wrapper.
     */
    object BitStream {

        /** Encode [bits] as a verified radix-2 stream. */
        fun encode(bits: Iterable<Boolean>): Stream {
            var stream = empty(Radix(2))
            for (bit in bits) stream = stream.append(if (bit) 1 else 0)
            return stream
        }

        /** Render a verified radix-2 [stream] as booleans. */
        fun render(stream: Stream): BooleanArray? {
            if (stream.radix != Radix(2)) return null
            val bits = BooleanArray(stream.length)
            var current = stream.link
            for (i in stream.length - 1 downTo 0) {
                val digitOrd = current.targetAsInt() ?: return null
                bits[i] = (digitOrd == 1)
                current = current.parentOrNull().nestedOrNull() ?: return null
            }
            return bits
        }

        /** Validate [link] as a radix-2 stream and render it as booleans. */
        fun render(link: NestedLink): BooleanArray? = link.asStream(Radix(2))?.let(::render)
    }

    /**
     * Octet stream codec over fixed-width grouped streams.
     *
     * Topology:
     *
     * ```text
     * stream -> DATA(radix / width)
     * ```
     *
     * Any fixed-width frame with `radix^width == 256` is an octet frame, for example `(2, 8)`, `(16, 2)` or
     * `(256, 1)`.
     * The codec exposes byte-oriented transport/rendering over data-layer stream topology; it is not a text codec.
     */
    object OctetStream {

        enum class BitOrder {
            MOST_SIGNIFICANT_FIRST, LEAST_SIGNIFICANT_FIRST;

            fun offsets(): IntProgression = when (this) {
                MOST_SIGNIFICANT_FIRST -> (BITS_PER_OCTET - 1) downTo 0
                LEAST_SIGNIFICANT_FIRST -> 0 until BITS_PER_OCTET
            }
        }

        /** Encode [bytes] as a fixed-width grouped binary stream. */
        fun encode(bytes: ByteArray, bitOrder: BitOrder = BitOrder.MOST_SIGNIFICANT_FIRST): FixedWidthGrouping
        = encode(bytes.size, bytes::get, bitOrder)

        /** Encode [bytes] as a fixed-width grouped binary stream. */
        fun encode(bytes: ByteString, bitOrder: BitOrder = BitOrder.MOST_SIGNIFICANT_FIRST): FixedWidthGrouping
        = encode(bytes.size, bytes::get, bitOrder)

        /**
         * Encode [bytes] as a radix-256 octet stream — **one digit (link) per byte** instead of one per bit.
         *
         * The lens it carries (`radix 256 / width 1`) is an octet frame just like the bit-granular `(2, 8)`, so
         * [render] decodes it to the identical bytes; this is purely the encoder's choice of radix. A body encoded
         * this way costs ~8× fewer stream links than the bit-granular form, which is what dominates a post's size.
         */
        fun encodeDirect(bytes: ByteArray): FixedWidthGrouping = encodeDirect(bytes.size, bytes::get)

        /** Encode [bytes] as a radix-256 octet stream — one digit (link) per byte. See [encodeDirect]. */
        fun encodeDirect(bytes: ByteString): FixedWidthGrouping = encodeDirect(bytes.size, bytes::get)

        private inline fun encodeDirect(size: Int, getByte: (Int) -> Byte): FixedWidthGrouping {
            var stream = empty(Radix(OCTET_CARDINALITY))
            for (index in 0 until size) stream = stream.append(getByte(index).toInt() and 0xff)
            return FixedWidthGrouping(stream, GroupWidth(1))
        }

        /** Try to render a fixed-width grouped octet stream as bytes. */
        fun render(link: NestedLink): ByteString? = FixedWidthGrouping.render(link)?.let(::render)

        /** Try to render a fixed-width grouped octet stream as bytes. */
        fun render(grouping: FixedWidthGrouping): ByteString? {
            val radix = grouping.framing.radix.value
            val width = grouping.framing.width.value
            if (!grouping.framing.exactly(OCTET_CARDINALITY)) return null
            if (grouping.stream.length % width != 0) return null

            val digits = grouping.stream.digitsOrNull() ?: return null
            val result = ByteArray(digits.size / width) { frame ->
                var value = 0
                repeat(width) { offset -> value = value * radix + digits[frame * width + offset] }
                value.toByte()
            }
            return ByteString(*result)
        }

        /** Try to render a radix-256 stream, where each digit is one octet, as bytes. */
        fun renderDirect(stream: Stream): ByteString? = render(FixedWidthGrouping(stream, GroupWidth(1)))

        /** Try to render the canonical binary octet lens used by [encode]. */
        fun renderCanonical(stream: Stream): ByteString? = render(FixedWidthGrouping(stream, GroupWidth(BITS_PER_OCTET)))

        private inline fun encode(size: Int, getByte: (Int) -> Byte, bitOrder: BitOrder): FixedWidthGrouping {
            val offsets = bitOrder.offsets()
            var bitStream = empty(Radix(2))
            for (byteIndex in 0 until size) {
                val value = getByte(byteIndex).toInt() and 0xff
                for (offset in offsets) {
                    bitStream = bitStream.append(if (((value ushr offset) and 1) != 0) 1 else 0)
                }
            }
            return FixedWidthGrouping(bitStream, GroupWidth(BITS_PER_OCTET))
        }

        private fun Stream.digitsOrNull(): IntArray? {
            val digits = IntArray(length)
            var current = link
            for (index in length - 1 downTo 0) {
                digits[index] = current.targetAsInt() ?: return null
                current = current.parentOrNull().nestedOrNull() ?: return null
            }
            return digits
        }

        private const val BITS_PER_OCTET = 8
        private const val OCTET_CARDINALITY = 256
    }
}
